/* eslint-disable camelcase, css-modules/no-undef-class, css-modules/no-unused-class, no-nested-ternary */
import { FC, useState, useEffect, type UIEvent, useCallback, useRef, useMemo, ReactNode } from 'react';
import {
    MRT_ColumnDef,
    MRT_Icons,
    MRT_PaginationState,
    MRT_TablePagination,
    MRT_Row,
    MRT_RowSelectionState,
    MaterialReactTable,
    useMaterialReactTable,
    MRT_TableInstance,
    MRT_Cell,
    MRT_ToggleRowActionMenuButton
} from 'material-react-table';
import { Box } from '@mui/material';
import { Icon } from 'learning-panel-components';
import { translate } from 'translations';
import { mixpanelTrack, gainsightTrack } from 'utils';
import Theme from '../theme';
import styles from './table.module.scss';

const Table: FC<TableProps> = (props: TableProps) => {
    const {
        data,
        onChangePage,
        perPage,
        isSelected,
        totalRows,
        isLoading,
        columnsDeclaration,
        isFetching,
        maxHeight,
        getRowId,
        onRowClickFunction,
        onSelectedChange,
        fetchNextPage,
        fetchMethod,
        totalFetched,
        searchString,
        selectAllStatus,
        showHeaders = 'true',
        markSelectedRows,
        noBorder,
        rowIdField = 'uuid',
        clearSelection,
        rowHeight = '2.75rem',
        enableRowAction,
        renderRowAction,
        setHoverId,
        dependencies,
        pointerCursor,
        renderEmptyState,
        addCreateRow = false,
        columsWithFilters,
        mixPanelMetaData,
        showTopPagination
    } = props;

    useEffect(() => {
        addCreateRow && table.setCreatingRow(true);
    }, [addCreateRow]);

    const columns = useMemo(() => {
        return columnsDeclaration.map(column => ({
            ...column,
            enableEditing: column.enableEditing || false
        }));
    }, [dependencies]);
    const tableContainerRef = useRef<HTMLTableElement>(null);

    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: fetchMethod === 'pagination' ? perPage || 10 : data.length
    });

    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
    const [rowSelections, setRowSelections] = useState<{ [key: string]: MRT_RowSelectionState }>({});

    useEffect(() => {
        if (selectAllStatus === 'all') {
            const newSelection: MRT_RowSelectionState = {};
            data.forEach((row: any) => {
                newSelection[getRowId(row, 0, row)] = true;
            });
            setRowSelection(newSelection);
        } else if (selectAllStatus === 'none') {
            setRowSelection({});
        }
    }, [selectAllStatus]);

    useEffect(() => {
        if (clearSelection) {
            setRowSelection({});
            setRowSelections({});
        }
    }, [clearSelection]);

    useEffect(() => {
        if (markSelectedRows && markSelectedRows.length > 0) {
            const areEqual = markSelectedRows.every((row: any) => rowSelection[row[rowIdField]]);
            if (!areEqual) {
                const newSelection: MRT_RowSelectionState = {};
                const tmpRowSelections = { ...rowSelections };
                markSelectedRows.forEach((selectedRow: MRT_RowSelectionState) => {
                    const id = String(selectedRow[rowIdField]);
                    newSelection[id] = true;
                    tmpRowSelections[id] = selectedRow;
                });

                setRowSelection(newSelection);
                setRowSelections(tmpRowSelections);
            }
        }
    }, [markSelectedRows]);

    useEffect(() => {
        Object.keys(rowSelection).map((row: any) => {
            if (!rowSelections[row]) {
                rowSelections[row] = table.getRow(row).original;
            }
        });
        const selectedRows = Object.keys(rowSelection).map((row: any) => rowSelections[row]);
        onSelectedChange?.(selectedRows);
    }, [rowSelection]);

    useEffect(() => {
        onChangePage?.(pagination);
    }, [pagination]);

    useEffect(() => {
        setPagination(prev => ({
            ...prev,
            pageSize: perPage || 10
        }));
    }, [perPage]);

    const calculationRowsPerPageOptions = (totalRows: number) => {
        const rowsPerPageOptions = [10, 25, 50, 100];
        const result = rowsPerPageOptions.filter(option => option <= totalRows);
        if (rowsPerPageOptions[result.length - 1] < totalRows && totalRows < 100)
            result.push(rowsPerPageOptions[result.length]);
        if (result.length === 1) {
            return [];
        }
        return result;
    };

    const fontAwesomeIcons: Partial<MRT_Icons> = {
        ChevronLeftIcon: () => <Icon name="arrowLeftPagination" className={styles['arrow-icon']} />,
        ChevronRightIcon: () => <Icon name="arrowRightPagination" className={styles['arrow-icon']} />,
        MoreHorizIcon: () => <Icon name="kebab" />
    };

    const onRowClick = (row: any) => {
        if (mixPanelMetaData) {
            if (mixPanelMetaData.tab === 'assign-content') {
                const mixpanelProperties: { [key: string]: any } = {};
                if (mixPanelMetaData.section === 'audience') {
                    mixpanelProperties.Items = row.original.additionalItems?.length || 0;
                } else {
                    mixpanelProperties.Items = row.original.userProgress?.total || 0;
                }
                mixpanelProperties.Tab = mixPanelMetaData.section;
                mixpanelTrack({
                    eventName: 'Assignment Row Clicked',
                    properties: mixpanelProperties
                });
                gainsightTrack('Assignment Row Clicked', mixpanelProperties);
            }
        }
        onRowClickFunction?.(row);
    };

    const fetchMoreOnBottomReached = useCallback(
        (containerRefElement?: HTMLDivElement | null) => {
            if (containerRefElement) {
                const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
                if (
                    scrollHeight - scrollTop - clientHeight < 20 &&
                    !isFetching &&
                    totalFetched &&
                    totalFetched < totalRows
                ) {
                    fetchNextPage?.();
                }
            }
        },
        [fetchNextPage, isFetching, totalFetched, totalRows]
    );

    useEffect(() => {
        if (fetchMethod !== 'pagination') {
            fetchMoreOnBottomReached(tableContainerRef.current);
        }
    }, [fetchMoreOnBottomReached, searchString]);

    const ROWS = translate('Rows');
    const SELECTED_ROWS = translate('SelectedRows', {
        selectedCount: String(Object.keys(rowSelection).length),
        rowCount: String(totalRows)
    });

    const getBackgroundRow = (row: MRT_Row<any>) => {
        if (row.getIsSelected()) {
            return 'linear-gradient(0deg, rgba(239, 233, 251, 0.50) 0%, rgba(239, 233, 251, 0.50) 100%) !important';
        }
        if (row.id === 'mrt-row-create') {
            return 'rgba(234, 228, 248, 0.3)';
        }
        if (row.original.backgroundColor) {
            return row.original.backgroundColor;
        }
        return undefined;
    };

    const table = useMaterialReactTable({
        data,
        columns,
        enableTopToolbar: true,
        positionToolbarAlertBanner: 'none',
        enableGlobalFilter: false,
        enableHiding: false,
        rowCount: totalRows,
        enableRowSelection: isSelected && (row => !row.original?.isDisabled),
        selectAllMode: 'all',
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilters: false,
        getRowId,
        enableSelectAll: true,
        enableEditing: true,
        editDisplayMode: 'cell',
        createDisplayMode: 'row',
        manualPagination: fetchMethod === 'pagination',
        enablePagination: fetchMethod === 'pagination',
        enableStickyHeader: true,
        enableFullScreenToggle: false,
        enableStickyFooter: true,
        enableDensityToggle: false,
        enableRowActions: enableRowAction,
        renderRowActions: ({
            row,
            table,
            cell
        }: {
            row: MRT_Row<any>;
            table: MRT_TableInstance<any>;
            cell: MRT_Cell<any>;
        }) => renderRowAction?.(row, table, cell),
        renderEmptyRowsFallback: () => renderEmptyState,
        positionActionsColumn: 'last',
        muiTableContainerProps: {
            ref: tableContainerRef,
            sx: {
                maxHeight,
                minHeight: 'max-content',
                overflow: 'auto',
                background: 'transparent',
                borderRadius: '7px 7px 0px 0px',
                border: '1px solid rgba(113, 101, 150, 0.20)',
                borderBottom: 'none',
                borderLeft: undefined
            },
            onScroll: (event: UIEvent<HTMLDivElement>) => fetchMoreOnBottomReached(event.target as HTMLDivElement)
        },
        muiTableHeadRowProps: {
            sx: {
                background: 'linear-gradient(83deg, rgba(242, 125, 177, 0.03) 3.03%, rgba(226, 216, 247, 0.05) 94.71%)',
                boxShadow: '0px 0px 8px 0px rgba(167, 167, 193, 0.24)',
                border: noBorder ? 'none' : undefined,
                borderBottom: noBorder ? 'none' : undefined,
                backdropFilter: 'blur(11px)'
            }
        },
        muiTableBodyRowProps: ({ row }) => ({
            sx: {
                '&:hover': {
                    background: row.getIsSelected()
                        ? '#BAA7D8'
                        : 'linear-gradient(83deg, rgba(226, 216, 247, 0.40) 2.82%, rgba(242, 125, 177, 0.05) 52.7%, rgba(226, 216, 247, 0.40) 94.71%) !important'
                },
                '&:hover td': {
                    backgroundColor: 'unset !important'
                },
                cursor: isSelected && pointerCursor ? 'pointer' : undefined,
                color: '#626262',
                height: rowHeight,
                zIndex: 'auto',
                fontFamily: 'Inter',
                overflow: 'visible'
            }
        }),

        layoutMode: 'grid',

        muiTableHeadCellProps: ({ column }) => ({
            sx: showHeaders
                ? {
                      background:
                          'linear-gradient(83deg, rgba(242, 125, 177, 0.03) 3.03%, rgba(226, 216, 247, 0.05) 94.71%)',
                      color: '#716596',
                      fontWeight: 'normal',
                      borderLeft:
                          column.id === 'mrt-row-actions' ||
                          column.id === 'action' ||
                          column.id === 'mrt-row-select' ||
                          noBorder
                              ? undefined
                              : '1px solid rgba(113, 101, 150, 0.20)',
                      height: '3rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: columsWithFilters && columsWithFilters[column.id] ? 'pointer' : undefined,
                      '&:hover': {
                          background:
                              columsWithFilters &&
                              columsWithFilters[column.id] &&
                              'linear-gradient(83deg, rgba(226, 216, 247, 0.40) 2.82%, rgba(242, 125, 177, 0.05) 52.7%, rgba(226, 216, 247, 0.40) 94.71%)'
                      }
                  }
                : {
                      display: 'none'
                  }
        }),
        muiTableBodyProps: () => ({
            sx: {
                '&.MuiTableRow-root:hover': {
                    background: 'none'
                },
                minHeight: 'auto'
            },
            onScroll: (event: UIEvent<HTMLDivElement>) => fetchMoreOnBottomReached(event.target as HTMLDivElement)
        }),
        muiTablePaperProps: {
            elevation: 0,
            sx: {
                boxShadow: 'none',
                background: 'transparent'
            }
        },
        muiTableHeadProps: {
            sx: {
                zIndex: '10',
                background: 'white',
                opacity: '1 !important'
            }
        },
        muiTableBodyCellProps: ({ row, column }) => ({
            ...(setHoverId !== undefined && { onMouseEnter: () => setHoverId?.(row.id) }),
            ...(setHoverId !== undefined && { onMouseLeave: () => setHoverId?.(undefined) }),
            onClick: () => onRowClick(row),
            sx: {
                '&:hover': {
                    outline: 'none'
                },
                cursor: isSelected && pointerCursor ? 'pointer' : undefined,
                borderLeft:
                    noBorder ||
                    column.id === 'mrt-row-actions' ||
                    column.id === 'mrt-row-select' ||
                    column.id === 'action'
                        ? undefined
                        : '1px solid rgba(113, 101, 150, 0.20)',

                color: '#626262',
                backgroundImage: 'unset !important',
                background: getBackgroundRow(row),
                height: rowHeight,
                zIndex: 'auto',
                fontFamily: 'Inter',
                overflow: 'visible'
            }
        }),
        muiTableProps: {
            sx: {
                background: 'transparent'
            }
        },
        muiPaginationProps: {
            sx: {
                '& .MuiPaginationItem-root': {
                    color: '#626262'
                },
                '& .MuiPaginationItem-root.Mui-selected': {
                    backgroundColor: '#EFE9FB',
                    borderRadius: '8px'
                },
                '& .MuiPaginationItem-root:hover': {
                    borderRadius: '8px'
                },
                '& .MuiPagination-root': {
                    width: '100%'
                }
            },
            showFirstButton: false,
            showLastButton: false,
            showRowsPerPage: totalRows > 10,
            rowsPerPageOptions: calculationRowsPerPageOptions(totalRows)
        },
        displayColumnDefOptions: {
            'mrt-row-select': {
                size: 10
            },
            'mrt-row-expand': {
                size: 10
            },
            'mrt-row-actions': {
                Cell: ({ row, table, cell }) => {
                    return row.id === 'mrt-row-create' ? (
                        <></>
                    ) : (
                        <MRT_ToggleRowActionMenuButton cell={cell} row={row} table={table} />
                    );
                },
                size: 30
            }
        },
        muiLinearProgressProps: ({ isTopToolbar }) => ({
            sx: {
                display: isTopToolbar ? 'block' : 'none',
                background: '#E7E7F4',
                color: 'primary',
                '& .MuiLinearProgress-colorPrimary': {
                    primaryColor: '#E7E7F4'
                }
            }
        }),
        renderTopToolbar: ({ table }) =>
            fetchMethod === 'pagination' && showTopPagination ? (
                <Box
                    sx={{
                        background: 'white',
                        position: 'relative',
                        mb: 1,
                        '& .MuiFormLabel-root': {
                            color: 'rgba(98, 98, 98, 0.70)',
                            fontFamily: 'Inter',
                            fontSize: '0.8125rem',
                            fontWeight: '400'
                        },
                        '& .MuiInputBase-root': {
                            color: 'rgba(98, 98, 98, 0.70)',
                            fontFamily: 'Inter',
                            fontSize: '0.8125rem',
                            fontWeight: '400',
                            border: '1px solid #D7D8DB',
                            borderRadius: '4px',
                            width: '4.375rem',
                            padding: '0px !important'
                        },
                        '& .MuiSelect-select': {
                            padding: '0.1rem 0.4rem',
                            color: '#626262',
                            fontFamily: 'Inter',
                            fontSize: '0.8125rem',
                            fontWeight: '400'
                        },
                        '& .MuiSelect-select:focus': {
                            backgroundColor: 'transparent'
                        },
                        '& .MuiTablePagination-root': {
                            justifyContent: totalRows > 10 ? 'space-between' : 'flex-end'
                        }
                    }}
                >
                    <MRT_TablePagination table={table} />
                </Box>
            ) : null,
        state: {
            isLoading,
            pagination,
            rowSelection,
            showProgressBars: isFetching,
            showLoadingOverlay: false
        },
        renderBottomToolbar: ({ table }) =>
            fetchMethod === 'pagination' ? (
                <Box
                    sx={{
                        background: 'white',
                        position: 'relative',
                        '& .MuiFormLabel-root': {
                            color: 'rgba(98, 98, 98, 0.70)',
                            fontFamily: 'Inter',
                            fontSize: '0.8125rem',
                            fontWeight: '400'
                        },
                        '& .MuiInputBase-root': {
                            color: 'rgba(98, 98, 98, 0.70)',
                            fontFamily: 'Inter',
                            fontSize: '0.8125rem',
                            fontWeight: '400',
                            border: '1px solid #D7D8DB',
                            borderRadius: '4px',
                            width: '4.375rem',
                            padding: '0px !important'
                        },
                        '& .MuiSelect-select': {
                            padding: '0.1rem 0.4rem',
                            color: '#626262',
                            fontFamily: 'Inter',
                            fontSize: '0.8125rem',
                            fontWeight: '400'
                        },
                        '& .MuiSelect-select:focus': {
                            backgroundColor: 'transparent'
                        },
                        '& .MuiTablePagination-root': {
                            justifyContent: totalRows > 10 ? 'space-between' : 'flex-end'
                        }
                    }}
                >
                    <MRT_TablePagination table={table} />
                </Box>
            ) : undefined,
        onPaginationChange: setPagination,
        onRowSelectionChange: setRowSelection,
        localization: {
            rowsPerPage: totalRows > 10 ? ROWS : undefined,
            selectedCountOfRowCountRowsSelected: SELECTED_ROWS,
            expand: '',
            actions: ''
        },
        initialState: {
            density: 'compact',
            columnVisibility: {
                learningUnit: true,
                'mrt-row-expand': false
            }
        },
        defaultDisplayColumn: {
            minSize: 10
        },
        paginationDisplayMode: 'pages',
        icons: fontAwesomeIcons,
        enableBottomToolbar: fetchMethod === 'pagination',
        filterFromLeafRows: true
    });

    return (
        <Theme>
            <div className={styles.table}>
                <MaterialReactTable key={pagination?.pageSize} table={table} />
            </div>
        </Theme>
    );
};

export default Table;

interface TableProps {
    data: any;
    onChangePage?: Function;
    isSelected: boolean;
    totalRows: number;
    isLoading: boolean;
    columnsDeclaration: MRT_ColumnDef<any>[];
    isFetching: boolean;
    getRowId: (originalRow: any, index: number, parentRow: MRT_Row<any>) => string;
    maxHeight: string;
    onRowClickFunction?: Function;
    onSelectedChange?: Function;
    fetchMethod: 'infinity' | 'pagination';
    fetchNextPage?: Function;
    totalFetched?: number;
    searchString: string;
    selectAllStatus?: 'all' | 'none' | 'indeterminate';
    showHeaders?: boolean;
    markSelectedRows?: any;
    noBorder?: boolean;
    rowIdField?: string;
    clearSelection?: boolean;
    rowHeight?: string;
    enableRowAction?: boolean;
    backgroundColor?: string;
    renderRowAction?: Function;
    setHoverId?: Function;
    dependencies?: any[];
    pointerCursor?: boolean;
    renderEmptyState?: ReactNode;
    addCreateRow?: boolean;
    columsWithFilters?: { [key: string]: boolean };
    mixPanelMetaData?: any;
    perPage?: number;
    showTopPagination?: boolean;
}
